import React, { useState, useEffect } from 'react';
import { Minimize2, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons, NoteCard } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const CompressPDF = () => {
    const [file, setFile] = useState(null);
    const [originalSize, setOriginalSize] = useState(0);
    const [compressionLevel, setCompressionLevel] = useState('medium');
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');
    const [compressionStats, setCompressionStats] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);
            setOriginalSize(selectedFile.size);
            toast.success('PDF loaded successfully');
        }
    };

    const compressPDF = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            // Load the PDF
            const arrayBuffer = await file.arrayBuffer();
            setProgress(30);

            const pdfDoc = await PDFDocument.load(arrayBuffer);
            setProgress(50);

            // Compression settings based on level
            const compressionSettings = {
                low: { objectCompression: false, streamCompression: false },
                medium: { objectCompression: true, streamCompression: false },
                high: { objectCompression: true, streamCompression: true }
            };

            setProgress(70);

            // Save with compression
            const compressedBytes = await pdfDoc.save({
                useObjectStreams: compressionSettings[compressionLevel].objectCompression,
                addDefaultPage: false,
            });

            setProgress(90);

            const blob = new Blob([compressedBytes], { type: 'application/pdf' });
            const compressedSize = blob.size;
            const reduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);

            setProgress(100);

            // Set preview
            const filename = `compressed-${file.name}`;
            setPreviewBlob(blob);
            setPreviewFilename(filename);
            setCompressionStats({ originalSize, compressedSize, reduction });

            toast.success(`PDF compressed! Size reduced by ${reduction}%`);

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Compression error:', error);
            toast.error(`Failed to compress PDF: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    // Generate live preview when compression level changes
    const generateLivePreview = async () => {
        if (!file) {
            setPreviewBlob(null);
            return;
        }

        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);

            const compressionSettings = {
                low: { objectCompression: false, streamCompression: false },
                medium: { objectCompression: true, streamCompression: false },
                high: { objectCompression: true, streamCompression: true }
            };

            const compressedBytes = await pdfDoc.save({
                useObjectStreams: compressionSettings[compressionLevel].objectCompression,
                addDefaultPage: false,
            });

            const blob = new Blob([compressedBytes], { type: 'application/pdf' });
            const filename = `compressed-${file.name}`;
            
            setPreviewBlob(blob);
            setPreviewFilename(filename);
            
            const compressedSize = blob.size;
            const reduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);
            setCompressionStats({ originalSize, compressedSize, reduction });
        } catch (error) {
            console.error('Preview generation error:', error);
            setPreviewBlob(null);
        }
    };

    // Auto-update preview when compression level changes
    useEffect(() => {
        if (file) {
            const timeoutId = setTimeout(() => {
                generateLivePreview();
            }, 500); // Debounce for 500ms
            return () => clearTimeout(timeoutId);
        }
    }, [compressionLevel, file, originalSize]);

    const compressionLevels = [
        {
            id: 'low',
            label: 'Low Compression',
            description: 'Minimal compression, best quality',
            reduction: '~10-20%'
        },
        {
            id: 'medium',
            label: 'Recommended',
            description: 'Balanced compression and quality',
            reduction: '~30-50%'
        },
        {
            id: 'high',
            label: 'High Compression',
            description: 'Maximum compression, smaller file',
            reduction: '~50-70%'
        }
    ];

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <Minimize2 size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Compress PDF
                </h1>
                <p className="tool-description">
                    Reduce PDF file size while maintaining quality. Choose your compression level and optimize your documents.
                </p>
            </div>

            {/* File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here to compress"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            <Badge variant="info">{formatFileSize(originalSize)}</Badge>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setFile(null);
                                setOriginalSize(0);
                            }}
                        >
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    {/* Compression Level Selection */}
                    <div className="spacing-section">
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-xl)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                Compression Level
                            </h3>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }} className="nested-cards-container">
                                {compressionLevels.map(level => (
                                    <Card
                                        key={level.id}
                                        variant={compressionLevel === level.id ? 'elevated' : 'outlined'}
                                        hoverable
                                        onClick={() => setCompressionLevel(level.id)}
                                        style={{
                                            cursor: 'pointer',
                                            border: compressionLevel === level.id
                                                ? '2px solid var(--color-accent)'
                                                : '1px solid var(--color-border)',
                                            backgroundColor: compressionLevel === level.id
                                                ? 'var(--color-accent-subtle)'
                                                : 'var(--color-surface)'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--spacing-2)',
                                                    marginBottom: 'var(--spacing-1)'
                                                }}>
                                                    <h4 style={{
                                                        fontWeight: 'var(--font-semibold)',
                                                        color: compressionLevel === level.id ? 'var(--color-accent)' : 'var(--color-text-primary)'
                                                    }}>
                                                        {level.label}
                                                    </h4>
                                                    {level.id === 'medium' && (
                                                        <Badge variant="success">Recommended</Badge>
                                                    )}
                                                </div>
                                                <p style={{
                                                    fontSize: 'var(--text-sm)',
                                                    color: 'var(--color-text-secondary)',
                                                    marginBottom: 'var(--spacing-1)'
                                                }}>
                                                    {level.description}
                                                </p>
                                                <p style={{
                                                    fontSize: 'var(--text-xs)',
                                                    color: 'var(--color-text-tertiary)'
                                                }}>
                                                    Expected reduction: {level.reduction}
                                                </p>
                                            </div>
                                            <div style={{
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '50%',
                                                border: compressionLevel === level.id
                                                    ? '6px solid var(--color-accent)'
                                                    : '2px solid var(--color-border)',
                                                flexShrink: 0
                                            }} />
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </Card>
                    </div>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <div className="progress-container">
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    {/* Preview and Compress Buttons */}
                    <div className="button-group" style={{ display: 'flex', gap: 'var(--spacing-3)' }}>
                        {previewBlob && (
                            <Button
                                variant="secondary"
                                size="lg"
                                onClick={() => setShowPreview(true)}
                                style={{ flex: 1 }}
                            >
                                <FileText size={20} style={{ marginRight: 'var(--spacing-2)' }} />
                                View Preview
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={compressPDF}
                            disabled={isProcessing}
                            loading={isProcessing}
                            style={{ flex: previewBlob ? 1 : 'none', width: previewBlob ? 'auto' : '100%' }}
                            fullWidth={!previewBlob}
                        >
                            <Minimize2 size={20} />
                            Compress PDF
                        </Button>
                    </div>

                    {/* Info */}
                    <NoteCard variant="info">
                        <strong>Note:</strong> Compression results may vary depending on the PDF content. PDFs with many images typically compress better than text-only documents.
                    </NoteCard>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && showPreview && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setShowPreview(false)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            if (compressionStats) {
                                toast.success(`Downloaded! Size reduced by ${compressionStats.reduction}%`);
                            }
                            setPreviewBlob(null);
                            setCompressionStats(null);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default CompressPDF;
